<template>
  <v-dialog
    :value="value"
    :fullscreen="$vuetify.breakpoint.mdAndDown"
    max-width="820"
    scrollable
    :persistent="opening"
    transition="dialog-bottom-transition"
    content-class="wc-appointment-search-dialog"
    @input="$emit('input', $event)">
    <v-card class="wc-search-card d-flex flex-column" :dark="isDarkMode" :light="!isDarkMode">
      <div class="wc-search-head px-5 pt-5 pb-4">
        <div class="d-flex align-center mb-4">
          <div class="wc-search-logo mr-3">
            <v-icon color="white" size="24">mdi-calendar-search</v-icon>
          </div>
          <div class="min-width-0 flex-grow-1">
            <div class="wc-search-eyebrow">Terminkalender</div>
            <div class="wc-search-title">Kalender durchsuchen</div>
          </div>
          <v-btn icon class="wc-search-close" aria-label="Suche schliessen" :disabled="opening" @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-text-field
          ref="searchInput"
          v-model="query"
          autofocus
          outlined
          hide-details
          clearable
          class="wc-search-input"
          prepend-inner-icon="mdi-magnify"
          :placeholder="searchPlaceholder"
          @keydown.enter.prevent="openFirstResult" />

        <div class="wc-search-filter-row d-flex align-center justify-space-between flex-wrap mt-3">
          <div class="wc-search-filter-actions d-flex align-center flex-wrap">
            <v-btn-toggle v-model="scope" mandatory dense class="wc-search-scope" color="primary">
              <v-btn value="day" class="text-none px-4">
                <v-icon left small>mdi-calendar-today</v-icon>
                {{ dayScopeLabel }}
              </v-btn>
              <v-btn value="all" class="text-none px-4">
                <v-icon left small>mdi-database-search-outline</v-icon>
                Gesamter Kalender
              </v-btn>
            </v-btn-toggle>

            <v-btn-toggle v-if="scope === 'all'" v-model="searchPeriod" mandatory dense class="wc-search-period" color="primary">
              <v-btn value="upcoming" class="text-none px-3">
                <v-icon left small>mdi-calendar-arrow-right</v-icon>
                Aktuell & Zukunft
              </v-btn>
              <v-btn value="past" class="text-none px-3">
                <v-icon left small>mdi-history</v-icon>
                Vergangenheit
              </v-btn>
            </v-btn-toggle>

            <button
              type="button"
              class="wc-search-task-toggle"
              :class="{ active: includeTasks }"
              :aria-pressed="includeTasks ? 'true' : 'false'"
              :title="includeTasks ? 'Aufgaben und Ämtli ausblenden' : 'Aufgaben und Ämtli in die Suche aufnehmen'"
              @click="includeTasks = !includeTasks">
              <v-icon size="16" color="inherit">{{ includeTasks ? "mdi-clipboard-check-outline" : "mdi-clipboard-off-outline" }}</v-icon>
              <span>Aufgaben & Ämtli</span>
              <small>{{ includeTasks ? "An" : "Aus" }}</small>
            </button>
          </div>

          <div class="wc-search-source d-flex align-center">
            <v-icon x-small class="mr-1" :color="scope === 'day' ? 'success' : 'primary'">
              {{ scope === "day" ? "mdi-lightning-bolt" : "mdi-server-outline" }}
            </v-icon>
            <span>{{ searchSourceLabel }}</span>
          </div>
        </div>
      </div>

      <v-divider />
      <v-progress-linear v-if="opening" indeterminate color="primary" height="3" />

      <v-card-text class="wc-search-results pa-0 flex-grow-1">
        <div v-if="scope === 'all' && trimmedQuery.length < 3" class="wc-search-empty">
          <div class="wc-search-empty-icon">
            <v-icon size="34" color="primary">mdi-text-search</v-icon>
          </div>
          <div class="wc-search-empty-title">Gesamten Kalender durchsuchen</div>
          <div class="wc-search-empty-copy">Bitte mindestens 3 Zeichen eingeben. Erst dann wird die Datenbank abgefragt.</div>
        </div>

        <div v-else-if="(scope === 'day' && dayLoading) || ((globalPending || globalLoading) && !globalResults.length)" class="pa-4">
          <v-skeleton-loader v-for="index in 5" :key="index" type="list-item-avatar-two-line" class="mb-2 rounded-lg" />
        </div>

        <div v-else-if="searchError && !displayedResults.length" class="wc-search-empty">
          <div class="wc-search-empty-icon error-state">
            <v-icon size="34" color="error">mdi-alert-circle-outline</v-icon>
          </div>
          <div class="wc-search-empty-title">Suche nicht möglich</div>
          <div class="wc-search-empty-copy">{{ searchError }}</div>
          <v-btn small outlined color="primary" class="mt-4 text-none" @click="performGlobalSearch(false)">Erneut versuchen</v-btn>
        </div>

        <div v-else-if="!displayedResults.length" class="wc-search-empty">
          <div class="wc-search-empty-icon">
            <v-icon size="34" color="grey">mdi-calendar-blank-outline</v-icon>
          </div>
          <div class="wc-search-empty-title">Keine Einträge gefunden</div>
          <div class="wc-search-empty-copy">{{ emptyResultCopy }}</div>
        </div>

        <template v-else>
          <div class="wc-search-result-head px-5 py-3 d-flex align-center justify-space-between">
            <span>{{ resultHeading }}</span>
            <v-chip x-small outlined :color="scope === 'day' ? 'success' : 'primary'">
              {{ hasMore && scope === "all" ? `${displayedResults.length} geladen` : `${displayedResults.length} Treffer` }}
            </v-chip>
          </div>

          <v-list two-line class="wc-search-list pa-2" :dark="isDarkMode" :light="!isDarkMode">
            <v-list-item
              v-for="item in displayedResults"
              :key="resultKey(item)"
              class="wc-search-result rounded-xl mb-1 px-3"
              :disabled="opening"
              @click="selectResult(item)">
              <div class="wc-result-accent" :style="{ backgroundColor: item.color || '#2196F3' }"></div>
              <v-list-item-avatar size="42" class="wc-result-icon mr-3" :style="resultIconStyle(item)">
                <v-icon :color="item.color || '#2196F3'" size="21">{{ item.icon || "mdi-calendar" }}</v-icon>
              </v-list-item-avatar>

              <v-list-item-content class="py-2 min-width-0">
                <div class="wc-result-title-row">
                  <v-list-item-title class="wc-result-primary text-truncate">{{ resultPrimary(item) }}</v-list-item-title>
                  <span class="wc-result-kind" :class="{ task: isTaskItem(item) }">{{ isTaskItem(item) ? "Ämtli" : "Termin" }}</span>
                </div>
                <v-list-item-subtitle class="wc-result-secondary text-truncate">
                  <span v-if="resultPrimary(item) !== (item.title || 'Termin')" class="font-weight-bold">{{ item.title || "Termin" }}</span>
                  <span v-if="resultPrimary(item) !== (item.title || 'Termin') && employeeName(item)"> · </span>
                  <span v-if="employeeName(item)">{{ employeeName(item) }}</span>
                  <span v-if="!employeeName(item) && item.description">{{ item.description }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>

              <div class="wc-result-meta text-right ml-3">
                <div class="wc-result-date">{{ resultDate(item) }}</div>
                <div class="wc-result-time">{{ item.start || "--:--" }}–{{ item.end || "--:--" }}</div>
                <div class="wc-result-status" :style="{ color: statusMeta(item).color }">
                  {{ statusMeta(item).label }}
                </div>
              </div>
              <v-icon small class="ml-3 wc-result-arrow">mdi-chevron-right</v-icon>
            </v-list-item>
          </v-list>

          <div v-if="scope === 'all' && (hasMore || globalLoading || searchError)" class="text-center px-4 pb-5 pt-2">
            <v-btn
              v-if="hasMore"
              outlined
              rounded
              color="primary"
              class="text-none font-weight-bold"
              :loading="globalLoading"
              @click="performGlobalSearch(true)">
              Weitere Treffer laden
            </v-btn>
            <v-btn
              v-else-if="searchError"
              small
              outlined
              rounded
              color="primary"
              class="text-none font-weight-bold"
              :loading="globalLoading"
              @click="performGlobalSearch(false)">
              Suche erneut versuchen
            </v-btn>
            <div v-if="searchError && displayedResults.length" class="error--text caption mt-2">{{ searchError }}</div>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"
import moment from "moment"

export default {
  name: "WorkCalendarAppointmentSearch",
  props: {
    value: { type: Boolean, default: false },
    currentDate: { type: String, default: "" },
    appointments: { type: Array, default: () => [] },
    isDarkMode: { type: Boolean, default: false },
    dayLoading: { type: Boolean, default: false },
    opening: { type: Boolean, default: false },
  },
  data() {
    return {
      query: "",
      scope: "day",
      searchPeriod: "upcoming",
      includeTasks: false,
      globalResults: [],
      globalLoading: false,
      globalPending: false,
      nextCursor: null,
      hasMore: false,
      personMatchesCapped: false,
      personLookupIncomplete: false,
      searchWindowMonths: 18,
      searchError: "",
      searchTimer: null,
      searchController: null,
      searchSequence: 0,
    }
  },
  computed: {
    trimmedQuery() {
      return String(this.query || "").trim()
    },
    dayScopeLabel() {
      if (this.currentDate === moment().format("YYYY-MM-DD")) return "Heute"
      return moment(this.currentDate, "YYYY-MM-DD").format("dd, DD.MM.")
    },
    searchPlaceholder() {
      return this.includeTasks ? "Kunde, Termin, Aufgabe / Ämtli, Mitarbeiter, Uhrzeit ..." : "Kunde, Termin, Mitarbeiter oder Uhrzeit ..."
    },
    filteredDayResults() {
      const needle = this.normalize(this.trimmedQuery)
      return [...this.appointments]
        .filter((item) => this.includeTasks || !this.isTaskItem(item))
        .filter((item) => !needle || this.appointmentHaystack(item).includes(needle))
        .sort((a, b) => String(a.start || "").localeCompare(String(b.start || "")))
    },
    displayedResults() {
      if (this.scope === "day") return this.filteredDayResults
      return this.includeTasks ? this.globalResults : this.globalResults.filter((item) => !this.isTaskItem(item))
    },
    resultHeading() {
      if (this.scope === "day") return this.trimmedQuery ? `Treffer am ${this.dayScopeLabel}` : `Einträge am ${this.dayScopeLabel}`
      return `${this.searchPeriod === "past" ? "Vergangene" : "Kommende"} Einträge zu „${this.trimmedQuery}“`
    },
    searchSourceLabel() {
      if (this.scope === "day") return "Sofort im geöffneten Tag"
      return this.searchPeriod === "past"
        ? `Neueste Treffer der letzten ${this.searchWindowMonths} Monate`
        : `Nächste Treffer der kommenden ${this.searchWindowMonths} Monate`
    },
    emptyResultCopy() {
      if (this.scope === "day") return "An diesem Tag passt kein Eintrag zur Suche."
      if (this.personMatchesCapped) {
        return "Sehr viele Personen passen zu diesem Namen. Bitte Vor- und Nachname eingeben, damit der richtige Termin eindeutig gefunden wird."
      }
      if (this.personLookupIncomplete) {
        return "Termin- und Aufgabentitel wurden durchsucht, die Personensuche war diesmal aber nur teilweise verfügbar. Bitte Vor- und Nachname eingeben oder die Suche erneut versuchen."
      }
      return this.searchPeriod === "past"
        ? "Keine passenden vergangenen Einträge. Wechsle zu „Aktuell & Zukunft“ oder versuche einen anderen Begriff."
        : "Keine passenden aktuellen oder künftigen Einträge. Für ältere Termine zu „Vergangenheit“ wechseln."
    },
  },
  watch: {
    value(isOpen) {
      if (!isOpen) {
        this.resetGlobalSearch()
        return
      }
      this.scope = "day"
      this.searchPeriod = "upcoming"
      this.query = ""
      this.includeTasks = false
      this.resetGlobalSearch()
      this.$nextTick(() => {
        window.setTimeout(() => this.$refs.searchInput?.focus(), 80)
      })
    },
    query() {
      if (this.scope === "all") this.scheduleGlobalSearch()
    },
    scope(nextScope) {
      this.resetGlobalSearch()
      if (nextScope === "all") this.scheduleGlobalSearch()
    },
    searchPeriod() {
      if (this.scope === "all") this.scheduleGlobalSearch()
    },
    includeTasks() {
      if (this.scope === "all") this.scheduleGlobalSearch()
    },
  },
  beforeDestroy() {
    this.resetGlobalSearch()
  },
  methods: {
    normalize(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("de-CH")
    },
    appointmentHaystack(item) {
      const raw = item?.rawData && typeof item.rawData === "object" ? item.rawData : {}
      return this.normalize(
        [
          this.isTaskItem(item) ? "Aufgabe Aufgaben Ämtli" : "Termin Termine",
          item.title,
          item.specialType,
          item.date,
          item.start,
          item.end,
          this.employeeSearchText(item),
          this.personSearchText(item.customer),
          this.personSearchText(item.customer2),
          this.personSearchText(item.noAccountData),
          this.personSearchText(item.noAccountData2),
          this.fullName(item.customerName),
          this.fullName(item.customer2Name),
          this.personSearchText(raw.customer),
          this.personSearchText(raw.customer2),
          this.personSearchText(raw.noAccountData),
          this.personSearchText(raw.noAccountData2),
          this.fullName(raw.customerName),
          this.fullName(raw.customer2Name),
        ].join(" "),
      )
    },
    isTaskItem(item) {
      return Boolean(item && (item.isTask || item.type === "task" || item.kind === "task"))
    },
    clearSearchTimer() {
      if (this.searchTimer) window.clearTimeout(this.searchTimer)
      this.searchTimer = null
    },
    resetGlobalSearch() {
      this.clearSearchTimer()
      this.searchSequence += 1
      this.globalResults = []
      this.globalLoading = false
      this.globalPending = false
      this.nextCursor = null
      this.hasMore = false
      this.personMatchesCapped = false
      this.personLookupIncomplete = false
      this.searchWindowMonths = 18
      this.searchError = ""
      if (this.searchController) this.searchController.abort()
      this.searchController = null
    },
    scheduleGlobalSearch() {
      this.clearSearchTimer()
      this.resetGlobalSearch()
      if (this.scope !== "all" || this.trimmedQuery.length < 3) return
      this.globalPending = true
      // Ein Browser-Abbruch beendet eine bereits laufende Mongo-Abfrage nicht
      // zuverlässig. So stapeln sich beim Tippen keine teuren Suchläufe.
      this.searchTimer = window.setTimeout(() => this.performGlobalSearch(false), 750)
    },
    async performGlobalSearch(append = false) {
      if (this.scope !== "all" || this.trimmedQuery.length < 3 || this.globalLoading) return
      const sequence = ++this.searchSequence
      const cursor = append ? this.nextCursor : null
      if (!append) {
        this.globalResults = []
        this.nextCursor = null
        this.hasMore = false
      }
      if (this.searchController) this.searchController.abort()
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null
      this.searchController = controller
      this.globalPending = false
      this.globalLoading = true
      this.searchError = ""
      try {
        const result = await api.shiftManagement.searchAppointments(
          this.trimmedQuery,
          cursor,
          25,
          controller?.signal || null,
          this.includeTasks,
          this.searchPeriod,
        )
        if (sequence !== this.searchSequence || this.scope !== "all") return
        const incoming = Array.isArray(result?.items) ? result.items : []
        this.globalResults = append ? [...this.globalResults, ...incoming] : incoming
        this.nextCursor = result?.nextCursor || null
        this.hasMore = Boolean(result?.hasMore && this.nextCursor)
        this.personMatchesCapped = Boolean(result?.personMatchesCapped)
        this.personLookupIncomplete = Boolean(result?.personLookupIncomplete)
        this.searchWindowMonths = Number(result?.searchWindowMonths) || 18
        const partialSources = Array.isArray(result?.partialSources) ? result.partialSources : []
        if (partialSources.includes("appointments") && partialSources.includes("tasks")) {
          this.searchError = "Die Kalendersuche war vorübergehend nicht vollständig. Bitte erneut versuchen."
        } else if (partialSources.includes("appointments")) {
          this.searchError = "Termine konnten vorübergehend nicht vollständig durchsucht werden. Angezeigt werden nur passende Aufgaben und Ämtli."
        } else if (partialSources.includes("tasks")) {
          this.searchError =
            "Aufgaben und Ämtli konnten vorübergehend nicht vollständig durchsucht werden. Die Termintreffer werden trotzdem angezeigt."
        }
      } catch (error) {
        if (sequence !== this.searchSequence) return
        if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError") return
        this.searchError = error?.response?.data?.message || "Der Kalender konnte nicht durchsucht werden."
      } finally {
        if (sequence === this.searchSequence) {
          this.globalLoading = false
          if (this.searchController === controller) this.searchController = null
        }
      }
    },
    resultKey(item) {
      return `${this.isTaskItem(item) ? "task" : "appointment"}-${item._id || item.id}-${item.date || this.currentDate}`
    },
    fullName(person) {
      if (!person) return ""
      if (typeof person === "string" || typeof person === "number") return String(person).trim()
      if (typeof person !== "object") return ""
      const explicitName = person.fullName || person.name || person.displayName || ""
      const splitName = `${person.vorname || person.firstName || ""} ${person.nachname || person.lastName || ""}`.trim()
      if (explicitName || splitName) return String(explicitName || splitName).trim()
      return this.fullName(person.user || person.employee || person.person)
    },
    personSearchText(person) {
      if (!person) return ""
      if (typeof person === "string" || typeof person === "number") return String(person)
      if (typeof person !== "object") return ""
      return [this.fullName(person), person.email, person.telefon, person.phone, person.mobile].filter(Boolean).join(" ")
    },
    employeeSearchText(item) {
      const raw = item?.rawData && typeof item.rawData === "object" ? item.rawData : {}
      return [
        item.employeeName,
        item.assignedEmployeeName,
        item.assignedEmployee,
        item.originalEmployee,
        raw.employeeName,
        raw.assignedEmployeeName,
        raw.assignedEmployee,
        raw.originalEmployee,
      ]
        .map((person) => this.fullName(person))
        .filter(Boolean)
        .join(" ")
    },
    resultPrimary(item) {
      const raw = item?.rawData && typeof item.rawData === "object" ? item.rawData : {}
      const people = [
        this.fullName(item.customer),
        this.fullName(item.customer2),
        this.fullName(item.noAccountData),
        this.fullName(item.noAccountData2),
        this.fullName(item.customerName),
        this.fullName(item.customer2Name),
        this.fullName(raw.customer),
        this.fullName(raw.customer2),
        this.fullName(raw.noAccountData),
        this.fullName(raw.noAccountData2),
      ].filter((name, index, names) => Boolean(name) && names.indexOf(name) === index)
      return people.length ? people.join(" + ") : item.title || (this.isTaskItem(item) ? "Aufgabe" : "Termin")
    },
    employeeName(item) {
      const raw = item?.rawData && typeof item.rawData === "object" ? item.rawData : {}
      const candidates = [
        item.employeeName,
        item.assignedEmployeeName,
        item.assignedEmployee,
        item.originalEmployee,
        raw.employeeName,
        raw.assignedEmployeeName,
        raw.assignedEmployee,
        raw.originalEmployee,
      ]
      return candidates.map((person) => this.fullName(person)).find(Boolean) || ""
    },
    resultDate(item) {
      const value = item.date || this.currentDate
      if (value === moment().format("YYYY-MM-DD")) return "Heute"
      return moment(value, "YYYY-MM-DD").format("dd, DD.MM.YY")
    },
    statusMeta(item) {
      const status = item?.isDone ? "done" : item?.status
      const statuses = {
        checkedin: { label: "Erschienen", color: "#00a152" },
        done: { label: "Erledigt", color: "#00a152" },
        completed: { label: "Erledigt", color: "#00a152" },
        noshow: { label: "Nicht gekommen", color: "#ef5350" },
        cancelled: { label: "Abgesagt", color: "#ef5350" },
        draft: { label: "Entwurf", color: "#fb8c00" },
        published: { label: "Geplant", color: "#78909c" },
        planned: { label: "Geplant", color: "#78909c" },
      }
      return statuses[status] || statuses.planned
    },
    resultIconStyle(item) {
      const color = item.color || "#2196F3"
      return { background: `${color}16`, border: `1px solid ${color}35` }
    },
    selectResult(item) {
      if (this.opening) return
      this.$emit("select", {
        id: item._id || item.id,
        date: item.date || this.currentDate,
        scope: this.scope,
        isTask: this.isTaskItem(item),
        kind: this.isTaskItem(item) ? "task" : "appointment",
      })
    },
    openFirstResult() {
      if (this.displayedResults.length) this.selectResult(this.displayedResults[0])
    },
  },
}
</script>

<style scoped>
.wc-search-card {
  --bg-card: #ffffff;
  --text-main: #17212b;
  --text-muted: #718096;
  --icon-muted: #94a3b8;
  min-height: min(700px, 80vh);
  max-height: 80vh;
  overflow: hidden;
  border-radius: 22px !important;
  background: var(--bg-card, #fff) !important;
}
.wc-search-card.theme--dark {
  --bg-card: #17191d;
  --text-main: #f5f7fa;
  --text-muted: rgba(255, 255, 255, 0.55);
  --icon-muted: rgba(255, 255, 255, 0.42);
}
.wc-search-head {
  flex: 0 0 auto;
  background: linear-gradient(145deg, rgba(33, 150, 243, 0.08), transparent 55%);
}
.wc-search-logo {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00a152, #007c3f);
  box-shadow: 0 8px 22px rgba(0, 161, 82, 0.22);
}
.wc-search-eyebrow {
  color: #00a152;
  font-size: 0.67rem;
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.wc-search-title {
  margin-top: 3px;
  color: var(--text-main, #17212b);
  font-size: 1.45rem;
  line-height: 1.15;
  font-weight: 900;
}
.wc-search-input ::v-deep .v-input__slot {
  min-height: 54px !important;
  border-radius: 15px !important;
  background: rgba(127, 127, 127, 0.045) !important;
}
.wc-search-input ::v-deep input {
  font-size: 1rem;
  font-weight: 600;
}
.wc-search-scope {
  border-radius: 10px;
  overflow: hidden;
  background: rgba(127, 127, 127, 0.08) !important;
}
.wc-search-period {
  border-radius: 10px;
  overflow: hidden;
  background: rgba(127, 127, 127, 0.08) !important;
}
.wc-search-scope .v-btn {
  min-height: 38px;
  font-size: 0.76rem;
  font-weight: 800;
}
.wc-search-period .v-btn {
  min-height: 38px;
  font-size: 0.7rem;
  font-weight: 800;
}
.wc-search-filter-row,
.wc-search-filter-actions {
  gap: 10px;
}
.wc-search-task-toggle {
  min-height: 38px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-muted, #718096);
  border: 1px solid rgba(127, 127, 127, 0.2);
  border-radius: 10px;
  background: rgba(127, 127, 127, 0.05);
  font-size: 0.72rem;
  font-weight: 850;
  transition: color 0.16s ease, border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;
}
.wc-search-task-toggle small {
  min-width: 25px;
  padding: 2px 5px;
  border-radius: 999px;
  background: rgba(127, 127, 127, 0.12);
  font-size: 0.58rem;
  font-weight: 900;
  text-transform: uppercase;
}
.wc-search-task-toggle:hover {
  color: var(--text-main, #17212b);
  transform: translateY(-1px);
}
.wc-search-task-toggle.active {
  color: #8e24aa;
  border-color: rgba(142, 36, 170, 0.34);
  background: rgba(142, 36, 170, 0.09);
}
.theme--dark .wc-search-task-toggle.active {
  color: #ce93d8;
}
.wc-search-task-toggle.active small {
  color: white;
  background: #8e24aa;
}
.wc-search-source {
  color: var(--text-muted, #718096);
  font-size: 0.7rem;
  font-weight: 700;
}
.wc-search-results {
  min-height: 0;
  overflow-y: auto;
  background: rgba(127, 127, 127, 0.018);
}
.wc-search-result-head {
  position: sticky;
  top: 0;
  z-index: 2;
  color: var(--text-muted, #718096);
  background: var(--bg-card, #fff);
  border-bottom: 1px solid rgba(127, 127, 127, 0.1);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.wc-search-list {
  background: transparent !important;
}
.wc-search-result {
  position: relative;
  min-height: 68px;
  overflow: hidden;
  border: 1px solid rgba(127, 127, 127, 0.11);
  background: rgba(127, 127, 127, 0.025);
  transition: transform 0.16s ease, border-color 0.16s ease, background 0.16s ease;
}
.wc-search-result:hover {
  z-index: 1;
  transform: translateY(-1px);
  border-color: rgba(33, 150, 243, 0.3);
  background: rgba(33, 150, 243, 0.055);
}
.wc-result-accent {
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 0;
  width: 3px;
  border-radius: 0 4px 4px 0;
}
.wc-result-icon {
  border-radius: 13px !important;
}
.wc-result-primary {
  color: var(--text-main, #17212b);
  font-size: 0.9rem;
  font-weight: 900;
}
.wc-result-title-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.wc-result-title-row .wc-result-primary {
  min-width: 0;
  flex: 1;
}
.wc-result-kind {
  flex: 0 0 auto;
  padding: 2px 6px;
  color: #1976d2;
  border: 1px solid rgba(25, 118, 210, 0.24);
  border-radius: 999px;
  background: rgba(25, 118, 210, 0.07);
  font-size: 0.55rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.wc-result-kind.task {
  color: #8e24aa;
  border-color: rgba(142, 36, 170, 0.28);
  background: rgba(142, 36, 170, 0.08);
}
.theme--dark .wc-result-kind {
  color: #90caf9;
}
.theme--dark .wc-result-kind.task {
  color: #ce93d8;
}
.wc-result-secondary {
  margin-top: 3px;
  color: var(--text-muted, #718096) !important;
  font-size: 0.72rem;
}
.wc-result-date {
  color: var(--text-main, #17212b);
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: capitalize;
}
.wc-result-time {
  color: var(--text-muted, #718096);
  font-size: 0.7rem;
  font-weight: 700;
}
.wc-result-status {
  margin-top: 1px;
  font-size: 0.62rem;
  font-weight: 900;
}
.wc-result-arrow {
  color: var(--icon-muted, #94a3b8) !important;
}
.wc-search-empty {
  min-height: 360px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.wc-search-empty-icon {
  width: 66px;
  height: 66px;
  margin-bottom: 16px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(33, 150, 243, 0.08);
}
.wc-search-empty-icon.error-state {
  background: rgba(244, 67, 54, 0.08);
}
.wc-search-empty-title {
  color: var(--text-main, #17212b);
  font-size: 1rem;
  font-weight: 900;
}
.wc-search-empty-copy {
  max-width: 420px;
  margin-top: 6px;
  color: var(--text-muted, #718096);
  font-size: 0.8rem;
  line-height: 1.5;
}
.min-width-0 {
  min-width: 0;
}
@media (max-width: 959px) {
  .wc-search-card {
    min-height: 100vh;
    min-height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0 !important;
  }
  .wc-search-head {
    padding: 18px 16px 14px !important;
  }
  .wc-search-result {
    min-height: 72px;
  }
}
@media (max-width: 599px) {
  .wc-search-source {
    display: none !important;
  }
  .wc-search-scope,
  .wc-search-period,
  .wc-search-period .v-btn,
  .wc-search-scope .v-btn {
    width: 100%;
  }
  .wc-search-filter-actions,
  .wc-search-task-toggle {
    width: 100%;
  }
  .wc-search-task-toggle {
    justify-content: center;
  }
  .wc-search-scope .v-btn {
    flex: 1 1 50%;
  }
  .wc-search-period .v-btn {
    flex: 1 1 50%;
  }
  .wc-result-meta {
    max-width: 92px;
  }
  .wc-result-arrow {
    display: none;
  }
}
</style>
